// src/components/admin/ImageOrganizer.tsx
import React, { useState } from 'react';
import {
    Grid,
    Package,
    ArrowRight,
    ArrowLeft,
    X,
    Star,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import type { ProductImage, ProductVariant } from '../../types';
import { getProductImageUrl } from '../../utils/productUtilities';

interface ImageOrganizerProps {
    images: ProductImage[];
    variants: ProductVariant[];
    expandedVariants: Record<number, boolean>;
    onExpandedVariantsChange: (expanded: Record<number, boolean>) => void;
    onImagesReorganized: (images: ProductImage[], variants: ProductVariant[]) => void;
    onClose: () => void;
}

const ImageOrganizer: React.FC<ImageOrganizerProps> = ({
    images,
    variants,
    expandedVariants,
    onExpandedVariantsChange,
    onImagesReorganized,
    onClose
}) => {
    const [draggedImage, setDraggedImage] = useState<ProductImage | null>(null);

    // Group images by variant
    const productImages = images.filter(img => !img.product_variant);
    const variantImagesMap = variants.reduce((acc, variant) => {
        acc[variant.id] = images.filter(img => img.product_variant === variant.id);
        return acc;
    }, {} as Record<number, ProductImage[]>);

    const handleDragStart = (e: React.DragEvent, image: ProductImage) => {
        setDraggedImage(image);
        e.dataTransfer.setData('text/plain', image.id.toString());
        e.currentTarget.classList.add('opacity-50');
    };

    const handleDragEnd = (e: React.DragEvent) => {
        e.currentTarget.classList.remove('opacity-50');
        setDraggedImage(null);
    };

    const handleDrop = (e: React.DragEvent, targetVariantId: number | null) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');

        if (!draggedImage) return;

        // Don't allow dropping if it's the same location
        if (draggedImage.product_variant === targetVariantId) return;

        // Update the image's variant association
        const updatedImages = images.map(img => {
            if (img.id === draggedImage.id) {
                return { ...img, product_variant: targetVariantId };
            }
            return img;
        });

        let updatedVariants = variants;
        if (targetVariantId !== null) {
            updatedVariants = variants.map(v => {
                if (v.id === targetVariantId) {
                    return { ...v, use_product_images: false };
                }
                return v;
            });
        }

        onImagesReorganized(updatedImages, updatedVariants);
        setDraggedImage(null);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.currentTarget.classList.add('drag-over');
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.currentTarget.classList.remove('drag-over');
    };

    const handleSetPrimary = (imageId: number) => {
        const updatedImages = images.map(img => ({
            ...img,
            is_primary: img.id === imageId
        }));
        onImagesReorganized(updatedImages);
    };

    const handleRemoveImage = (imageId: number) => {
        const updatedImages = images.filter(img => img.id !== imageId);
        onImagesReorganized(updatedImages);
    };

    const toggleVariant = (variantId: number) => {
        onExpandedVariantsChange({
            ...expandedVariants,
            [variantId]: !expandedVariants[variantId]
        });
    };

    const renderImageCard = (image: ProductImage, showVariantBadge = false) => (
        <div
            key={image.id}
            draggable
            onDragStart={(e) => handleDragStart(e, image)}
            onDragEnd={handleDragEnd}
            className="relative group border rounded-lg p-2 bg-white hover:shadow-md transition-shadow cursor-move"
        >
            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img
                    src={getProductImageUrl(image.image_url)}
                    alt={image.alt_text || 'Product image'}
                    className="w-full h-full object-cover"
                />
            </div>

            {image.is_primary && (
                <div className="absolute top-2 left-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full flex items-center">
                    <Star size={12} className="mr-1 fill-current" /> PRIMARY
                </div>
            )}

            {showVariantBadge && image.product_variant && (
                <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full m-2">
                    Variant
                </div>
            )}

            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex gap-2">
                    {!image.is_primary && (
                        <button
                            onClick={() => handleSetPrimary(image.id)}
                            className="p-2 bg-white rounded-full hover:bg-yellow-100"
                            title="Set as primary"
                        >
                            <Star size={16} className="text-yellow-600" />
                        </button>
                    )}
                    <button
                        onClick={() => handleRemoveImage(image.id)}
                        className="p-2 bg-white rounded-full hover:bg-red-100"
                        title="Remove"
                    >
                        <X size={16} className="text-red-600" />
                    </button>
                </div>
            </div>
        </div>
    );

    const renderDropZone = (variantId: number | null, title: string, count: number) => (
        <div
            onDrop={(e) => handleDrop(e, variantId)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 transition-colors min-h-[200px] drag-target"
        >
            <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-700">{title}</h4>
                <span className="text-sm text-gray-500">{count} images</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {(variantId === null ? productImages : variantImagesMap[variantId] || []).map(img =>
                    renderImageCard(img, variantId !== null)
                )}
            </div>

            {((variantId === null && productImages.length === 0) ||
                (variantId !== null && (!variantImagesMap[variantId] || variantImagesMap[variantId].length === 0))) && (
                    <div className="text-center py-8 text-gray-400">
                        <Package size={32} className="mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Drop images here</p>
                    </div>
                )}
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                        <div className="flex items-center">
                            <Grid className="h-5 w-5 text-gray-500 mr-2" />
                            <h3 className="text-lg font-medium text-gray-900">
                                Organize Images
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                        <div className="space-y-6">
                            {/* Instructions */}
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <p className="text-sm text-blue-700">
                                    Drag and drop images between sections to organize them.
                                    Product-level images show for all variants. Variant-specific images show only for that variant.
                                </p>
                            </div>

                            {/* Product Images Drop Zone */}
                            {renderDropZone(null, 'Product Images (All Variants)', productImages.length)}

                            {/* Variant Images Drop Zones */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-gray-700">Variant Images</h4>
                                {variants.map(variant => (
                                    <div key={variant.id} className="border rounded-lg">
                                        <button
                                            type="button"
                                            onClick={() => toggleVariant(variant.id)}
                                            className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-t-lg"
                                        >
                                            <div className="flex items-center">
                                                <Package size={16} className="text-gray-500 mr-2" />
                                                <span className="font-medium">{variant.name}</span>
                                                <span className="ml-2 text-sm text-gray-500">
                                                    SKU: {variant.full_sku}
                                                </span>
                                            </div>
                                            {expandedVariants[variant.id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                        </button>

                                        {expandedVariants[variant.id] && (
                                            <div className="p-4">
                                                {renderDropZone(variant.id, variant.name, variantImagesMap[variant.id]?.length || 0)}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
        .drag-over {
          border-color: #3b82f6 !important;
          background-color: #eff6ff !important;
        }
      `}</style>
        </div>
    );
};

export default ImageOrganizer;